import React, { useState, useEffect } from "react";
import { Box, TextField, Button, Typography, Avatar, Container, Fab } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

import logoImage from "../../assets/images/logo.png";
import callApi from "../../services/callApi";
import Loader from "../../components/Loader/Loader";
import ReferralBanner from "./components/ReferralBanner";
import { limpiarBaulSiExpirado, limpiarBaul, leerBaul } from "../../services/referralBaul";
// ─── Variables Flotantes Configurable WhatsApp ──────────────────────────────
const WSP_BOTTOM_XS = "15%";
const WSP_BOTTOM_MD = "33%";
const WSP_RIGHT_XS = "16px";
const WSP_RIGHT_MD = "4%";

// ─── Links de WhatsApp diferenciados por contexto de error ───────────────────
const WSP_HREF_SIS = "https://wa.me/56920809101?text=Hola,%20me%20estoy%20registrando%20en%20Clinifica%20y%20mi%20RUT%20no%20aparece%20en%20el%20Registro%20Nacional%20de%20Prestadores%20de%20Salud.%20%C2%BFMe%20pueden%20ayudar%3F";
const WSP_HREF_ADIPA = "https://wa.me/56920809101?text=Hola,%20me%20estoy%20registrando%20en%20Clinifica%20a%20trav%C3%A9s%20de%20ADIPA%20y%20mi%20RUT%20no%20fue%20validado%20en%20el%20sistema%20de%20ADIPA.%20%C2%BFMe%20pueden%20ayudar%3F";

// ─── Componentes y estilos compartidos ──────────────────────────────────────

const cardSx = {
  width: "100%",
  p: { xs: "20px", md: "28px" },
  borderRadius: { xs: "16px", md: "20px" },
  background: "#ffffff",
  boxShadow: "0 2px 4px rgba(0,0,0,0.03), 0 8px 16px rgba(0,0,0,0.03), 0 12px 24px rgba(0,0,0,0.05)",
  border: "1px solid #eaeaea",
  boxSizing: "border-box",
};

const inputSx = {
  mb: 1,
  "& .MuiInputBase-root": {
    height: { xs: "44px", md: "46px" },
    backgroundColor: "#f7f9fc",
    fontFamily: "Quicksand-Regular",
    borderRadius: "10px",
    fontSize: { xs: "14px", md: "15px" },
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    "& fieldset": { borderColor: "#e4e8ef" },
    "&:hover fieldset": { borderColor: "#a8b4c8" },
    "&.Mui-focused fieldset": { borderColor: "#1877f2", borderWidth: "1.5px" },
  },
  "& .MuiInputBase-input": { padding: "10px 14px", fontSize: { xs: "14px", md: "15px" } },
  "& .MuiInputLabel-root": {
    transform: "translate(14px, 11px) scale(1)",
    fontFamily: "Quicksand-Regular",
    fontSize: { xs: "14px", md: "15px" },
  },
  "& .MuiInputLabel-shrink": { transform: "translate(14px, -6px) scale(0.75)" },
  "& .MuiInputLabel-asterisk": { display: "none" },
};

const btnSx = {
  mt: 2,
  height: { xs: "46px", md: "48px" },
  borderRadius: "10px",
  bgcolor: "#1877f2",
  fontFamily: "Quicksand-Bold",
  fontSize: { xs: "15px", md: "16px" },
  textTransform: "none",
  boxShadow: "0 4px 12px rgba(24, 119, 242, 0.28)",
  transition: "all 0.2s ease",
  "&:hover": { bgcolor: "#166fe5", boxShadow: "0 6px 16px rgba(24, 119, 242, 0.38)" },
  "&.Mui-disabled": { bgcolor: "rgba(24, 119, 242, 0.35)", color: "white", boxShadow: "none" },
};

// ─── Paso 1: Validación de RUT ───────────────────────────────────────────────

const RutValidation = ({ onSuccess = () => { } }) => {
  const [rut, setRut] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [showWspButton, setShowWspButton] = useState(false);
  const [wspHref, setWspHref] = useState(WSP_HREF_SIS);
  const [showBotonContinuarSinAdipa, setShowBotonContinuarSinAdipa] = useState(false);
  const [datosRutAprobadoSIS, setDatosRutAprobadoSIS] = useState(null);

  const handleContinuarSinAdipa = () => {
    limpiarBaul();
    if (datosRutAprobadoSIS) {
      onSuccess(datosRutAprobadoSIS);
    }
  };

  // Limpiar baúl si lleva más de 28 días sin registrarse (ANTES de renderizar children y leer baúl)
  limpiarBaulSiExpirado();

  useEffect(() => {
    if (window.fbq) window.fbq("trackCustom", "inicioregistro");
    // Preload de la foto del referido para que el navegador la cachee
    // y no se vuelva a descargar en los pasos siguientes
    const baul = leerBaul();
    const fotoUrl = baul?.foto || null;
    if (fotoUrl) {
      const img = new Image();
      img.src = fotoUrl;
    }
  }, []);

  const formatRut = (value) => {
    let clean = value.replace(/[^0-9kK]+/g, "").toUpperCase();
    if (clean.length > 1) {
      const body = clean.slice(0, -1);
      const dv = clean.slice(-1);
      clean = body.replace(/\B(?=(\d{3})+(?!\d))/g, ".") + "-" + dv;
    }
    return clean;
  };

  const isRutValid = (r) => r.replace(/[^0-9kK]/g, "").length >= 3;

  const esReferidoAdipa = () => {
    const baul = leerBaul();
    return baul?.esAdipa === true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setShowError(false);
    setShowWspButton(false);
    setShowBotonContinuarSinAdipa(false);
    setWspHref(WSP_HREF_SIS);
    try {
      // ── Llamadas paralelas cuando el referido es ADIPA ──────────────────────
      if (esReferidoAdipa()) {
        const [resultRut, resultAdipa] = await Promise.allSettled([
          callApi(`/registro/validarProfesional?rut=${rut}`, "get"),
          callApi(`/agenda/validarAdipa?rut=${rut}`, "get"),
        ]);

        // Prioridad 1: errores de la API de RUT
        if (resultRut.status === "rejected") {
          const errorMsg = resultRut.reason?.response?.data?.message ||
            "El RUT no se encuentra en el Registro Nacional de Prestadores Individuales de Salud";
          setErrorMessage(errorMsg);
          setShowError(true);
          if (errorMsg.includes("Registro Nacional") || errorMsg.includes("WhatsApp")) {
            setShowWspButton(true);
            // wspHref ya está en WSP_HREF_SIS (default)
          }
          return;
        }

        const responseRut = resultRut.value;
        if (!responseRut?.datos_profesional) {
          const errorMsg = responseRut?.message || "Error en la validación del RUT";
          setErrorMessage(errorMsg);
          setShowError(true);
          if (errorMsg.includes("Registro Nacional")) {
            setShowWspButton(true);
            // wspHref ya está en WSP_HREF_SIS (default)
          }
          return;
        }

        // Prioridad 2: RUT válido → verificar si está en ADIPA
        // Si la API de ADIPA falló por red, la dejamos pasar (fail-open)
        if (resultAdipa.status === "fulfilled") {
          const responseAdipa = resultAdipa.value;
          if (responseAdipa?.en_adipa === false) {
            setErrorMessage("No encontramos tu RUT registrado en ADIPA, con tu cédula validada en la web de ADIPA. Si crees que estamos equivocados, contáctanos por WhatsApp.");
            setShowError(true);
            setShowWspButton(true);
            setWspHref(WSP_HREF_ADIPA);
            
            setDatosRutAprobadoSIS({ ...responseRut, rut, datos_profesional: { ...responseRut.datos_profesional, rut } });
            setShowBotonContinuarSinAdipa(true);
            return;
          }
        }

        // Todo OK: avanzar
        onSuccess({ ...responseRut, rut, datos_profesional: { ...responseRut.datos_profesional, rut } });
        return;
      }

      // ── Flujo normal (sin ADIPA) ────────────────────────────────────────────
      const response = await callApi(`/registro/validarProfesional?rut=${rut}`, "get");
      if (response?.datos_profesional) {
        onSuccess({ ...response, rut, datos_profesional: { ...response.datos_profesional, rut } });
      } else {
        const errorMsg = response?.message || "Error en la validación del RUT";
        setErrorMessage(errorMsg);
        setShowError(true);
        if (errorMsg.includes("Registro Nacional")) {
          setShowWspButton(true);
        }
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || "El RUT no se encuentra en el Registro Nacional de Prestadores Individuales de Salud";
      setErrorMessage(errorMsg);
      setShowError(true);
      if (errorMsg.includes("Registro Nacional") || errorMsg.includes("WhatsApp")) {
        setShowWspButton(true);
        // wspHref ya está en WSP_HREF_SIS (reseteado al inicio de handleSubmit)
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: "100vh",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#ffffff",
      py: { xs: 3, md: 4 },
      position: "relative",
    }}>
      <Loader isLoading={isLoading} />

      <Container maxWidth="xs" sx={{ px: { xs: 2, md: 2 } }}>
        {/* Card principal */}
        <Box sx={cardSx}>
          <Box sx={{ mb: { xs: 2, md: 2.5 }, display: "flex", justifyContent: "center" }}>
            <img src={logoImage} alt="Clinifica" style={{ height: "34px" }} />
          </Box>

          <Typography sx={{ fontFamily: "Poppins-SemiBold", fontSize: { xs: "18px", md: "20px" }, color: "#1c1c1c", mb: "6px" }}>
            Crea tu cuenta 🚀
          </Typography>
          <Typography sx={{ fontFamily: "Quicksand-Regular", fontSize: { xs: "13px", md: "14px" }, color: "#6b7280", mb: { xs: "20px", md: "24px" }, lineHeight: 1.5 }}>
            Ingresa tu RUT para verificar que eres profesional de la salud
          </Typography>

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="rut"
              label="RUT"
              name="rut"
              autoComplete="off"
              autoFocus
              value={rut}
              onChange={(e) => setRut(formatRut(e.target.value))}
              placeholder="XX.XXX.XXX-X"
              sx={inputSx}
            />

            <Button type="submit" fullWidth variant="contained" disabled={!isRutValid(rut)} sx={btnSx}>
              Continuar
            </Button>

            {showError && (
              <Typography sx={{ color: "#d32f2f", fontSize: { xs: "12px", md: "13px" }, fontFamily: "Quicksand-Regular", textAlign: "center", mt: "12px", p: "12px", bgcolor: "rgba(211, 47, 47, 0.07)", borderRadius: "10px", lineHeight: 1.4 }}>
                {errorMessage}
              </Typography>
            )}

            <Box sx={{ display: "flex", justifyContent: "center", mt: "16px" }}>
              <Button
                href="https://pro.Clinifica.com"
                target="_blank"
                variant="text"
                sx={{ color: "#1877f2", textTransform: "none", fontFamily: "Quicksand-Bold", fontSize: { xs: "13px", md: "14px" }, p: 0, "&:hover": { backgroundColor: "transparent", textDecoration: "underline" } }}
              >
                Iniciar sesión
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Banner referido */}
        <Box sx={{ mt: "12px" }}>
          <ReferralBanner />
        </Box>

        {showBotonContinuarSinAdipa && (
          <Box sx={{ mt: "12px" }}>
            <Button
              onClick={handleContinuarSinAdipa}
              fullWidth
              sx={{
                height: { xs: "46px", md: "48px" },
                borderRadius: "10px",
                bgcolor: "#ffffff",
                color: "#1877f2",
                border: "1px solid #ffffff",
                fontFamily: "Quicksand-Regular",
                fontSize: { xs: "15px", md: "16px" },
                textTransform: "none",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                transition: "all 0.2s ease",
                "&:hover": { bgcolor: "#f0f7fe", boxShadow: "0 4px 12px rgba(24, 119, 242, 0.15)" },
              }}
            >
              Continuar sin descuento ADIPA
            </Button>
          </Box>
        )}
      </Container>

      {/* Botón Flotante Reactivo de WhatsApp */}
      {showWspButton && (
        <Fab
          color="primary"
          aria-label="whatsapp"
          href={wspHref}
          target="_blank"
          sx={{
            position: "fixed",
            bottom: { xs: WSP_BOTTOM_XS, md: WSP_BOTTOM_MD },
            right: { xs: WSP_RIGHT_XS, md: WSP_RIGHT_MD },
            backgroundColor: "#25D366",
            "&:hover": { backgroundColor: "#1EBE5D" },
            animation: "slideUp 1.5s ease-out forwards, pulse 2s infinite",
            "@keyframes pulse": {
              "0%": { boxShadow: "0 0 0 0 rgba(37, 211, 102, 0.7)" },
              "70%": { boxShadow: "0 0 0 10px rgba(37, 211, 102, 0)" },
              "100%": { boxShadow: "0 0 0 0 rgba(37, 211, 102, 0)" },
            },
            "@keyframes slideUp": {
              "0%": { opacity: 0, transform: "translateY(100vh)" },
              "100%": { opacity: 1, transform: "translateY(0)" },
            },
            zIndex: 1000,
          }}
        >
          <WhatsAppIcon sx={{ color: "white", fontSize: "28px" }} />
        </Fab>
      )}
    </Box>
  );
};

export default RutValidation;
